import {Device as CapacitorDevice} from "@capacitor/device";
import {CapacitorIBeaconScanning} from "@moovement/capacitor-ibeacon-scanning";
import {Component, computed, effect, inject, OnDestroy, Signal, signal} from '@angular/core';
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {NgClass} from '@angular/common';
import {DataSubscriptionService} from 'src/app/infrastructure/data-subscription.service';
import {idToUuid, uuidToId} from 'src/app/domain/util/uuid/uuid';
import {BackButtonComponent} from "src/app/presentation/main-view/back-button/back-button.component";
import {CloseButtonComponent} from "src/app/presentation/main-view/close-button/close-button.component";
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {Animal} from 'src/app/infrastructure/moovement-api/models/asset';
import {
  AnimalButtonComponent
} from 'src/app/presentation/main-view/information-center/animals/animal-button/animal-button.component';
import {
  UnassignedTagButtonComponent
} from 'src/app/presentation/main-view/information-center/unassigned-tags/unassigned-tag-button/unassigned-tag-button.component';
import {DeviceInRangeStore} from 'src/app/domain/devices/device-in-range.store';
import {ascendingRssi} from 'src/app/domain/devices/device-in-range';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";

const scanDuration = 30000;

interface AugmentedDeviceInRange {
  rssi: number,
  id: string,
  animal?: Animal
}

@Component({
  selector: 'app-nearby-ble-tags',
  imports: [
    NgClass,
    AnimalButtonComponent,
    BackButtonComponent,
    CloseButtonComponent,
    UnassignedTagButtonComponent,
    GenericButtonComponent
],
  templateUrl: './nearby-ble-tags.component.html',
  styleUrl: './nearby-ble-tags.component.css'
})
export class NearbyBleTagsComponent implements OnDestroy {

  initialized = signal(false);
  showAllResults = signal(true);
  scanning = signal(false);

  augmentedDevicesInRange: Signal<AugmentedDeviceInRange[]> = computed(() => {
    const deviceIdToAnimals = this.animalStore.deviceIdToAnimal();
    let augmentedDevices = Array.from(this.deviceInRangeStore.devicesInRange().values())
      .sort(ascendingRssi)
      .map(deviceInRange => {
        const deviceId = deviceInRange.id;
        return {
          id: deviceInRange.id,
          rssi: deviceInRange.rssi,
          animal: deviceIdToAnimals.get(deviceInRange.id)
        };
      });

    if (!this.showAllResults()) {
      augmentedDevices = augmentedDevices.slice(0, 1);
    }

    return augmentedDevices;
  });

  private animalStore = inject(AnimalsStore);
  private deviceStore = inject(DeviceStore);
  private deviceInRangeStore = inject(DeviceInRangeStore);

  private scheduledStop?: number;
  private uuids: string[] = [];

  constructor(
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService
  ) {
    effect(() => {
      const tags = this.deviceStore.assignedTags();
      this.uuids = tags.map(tag => tag.id).map(id => idToUuid(id));
    });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.scheduledStop) {
      window.clearTimeout(this.scheduledStop);

      this.stopScan();
    }
  }

  async startScan() {
    this.scanning.set(true);

    const info = await CapacitorDevice.getInfo();
    const operatingSystem = info.operatingSystem;

    if (!['ios', 'android'].includes(operatingSystem)) {
      this.scanning.set(false);
      return;
    };

    this.startScanForUuids(this.uuids);
  }

  toggleShowAllResults() {
    this.showAllResults.update(value => !value);
  }

  clearResults() {
    this.deviceInRangeStore.reset();
  }

  private async startScanForUuids(uuids: string[]) {
    const permissions = await CapacitorIBeaconScanning.requestPermissions();
    if (!permissions.granted) return;

    CapacitorIBeaconScanning.addListener('onScanResult', result => {
      result.scanResults.forEach(scanResult => {
        const uuid = scanResult.uuid;
        this.processScanResult(uuid, scanResult.rssi);
      });
    });

    const scanFilters = uuids.map(uuid => ({ uuid }));

    CapacitorIBeaconScanning.startScanning({ scanFilters });

    this.scheduledStop = window.setTimeout(() => {
      this.stopScan();
    }, scanDuration);
  }

  private async stopScan() {
    await CapacitorIBeaconScanning.stopScanning();
    await CapacitorIBeaconScanning.removeAllListeners();
    this.scanning.set(false);
  }

  private async processScanResult(uuid: string, rssi: number) {
    if (rssi === 0) return;

    const deviceId = uuidToId(uuid);

    this.deviceInRangeStore.addDevice(deviceId, rssi);
  }
}
